import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';

import Preloader from 'ui-lib/components/Preloader';
import { createSx } from 'ui-lib/utils';
import defaultLogo from 'web-shared/media/Overhaul_Logo_DarkBackground_RGB 2.svg?url';
import useRequest from 'web-shared/hooks/useRequest';

import { getPublicCompanyData } from '../../../../../../../frontend/public/api/company';

const sx = createSx({
  root: {
    minHeight: '100vh',
    backgroundColor: 'neutral.fixed',
    textAlign: 'center',
    boxSizing: 'border-box',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    width: '100%',
    minWidth: 360,
    maxWidth: 420,
    p: 3,
    boxSizing: 'border-box',
    alignItems: 'center',
  },
  overhaulLogo: {
    width: '100%',
    maxWidth: 321,
    height: 26,
    objectFit: 'contain',
  },
  formCard: {
    width: '100%',
    p: 4,
    backgroundColor: 'neutral.surface',
    border: '1px solid',
    borderColor: 'neutral.outlineVariant',
    borderRadius: 1,
    boxShadow: 1,
    boxSizing: 'border-box',
    textAlign: 'left',
  },
  formHeader: {
    width: '100%',
    textAlign: 'center',
    alignItems: 'center',
  },
  companyLogoContainer: {
    width: '100%',
    height: 88,
    p: 1.5,
    overflow: 'hidden',
    boxSizing: 'border-box',
    alignItems: 'center',
    justifyContent: 'center',
  },
  companyLogo: {
    width: '100%',
    maxWidth: '100%',
    height: 64,
    maxHeight: 64,
    objectFit: 'contain',
  },
  title: {
    textTransform: 'none',
    color: 'neutral.onSurfaceVariant',
  },
  subtitle: {
    textAlign: 'center',
    textTransform: 'none',
    color: 'neutral.onSurfaceVariant',
  },
});

interface AuthLayoutData {
  companySubdomain: string | null;
}

interface Props {
  children: (data: AuthLayoutData) => React.ReactNode;
  customLogo?: React.ReactNode;
  loading?: boolean;
  onSubmit: React.SubmitEventHandler<HTMLFormElement>;
  subtitle?: React.ReactNode;
  title: React.ReactNode;
}

const AuthLayout = ({
  children,
  customLogo,
  loading,
  onSubmit,
  subtitle,
  title,
}: Props) => {
  const { t } = useTranslation();
  const companyDataRequest = useRequest(getPublicCompanyData, null);

  if (companyDataRequest.loading || loading) return <Preloader />;

  const handleLogoError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    if (event.currentTarget.src !== defaultLogo) {
      event.currentTarget.src = defaultLogo;
    }
  };

  return (
    <Stack component='form' onSubmit={onSubmit} sx={sx.root}>
      <Stack sx={sx.content} spacing={6}>
        <Box
          component='img'
          alt={t('auth.alt_overhaulLogo')}
          src={defaultLogo}
          sx={sx.overhaulLogo}
          data-test-id='overhaul-logo-image'
        />
        <Stack sx={sx.formCard} spacing={3}>
          <Stack sx={sx.formHeader}>
            {!!(customLogo ?? companyDataRequest.data?.logo?.url) && (
              <Stack sx={sx.companyLogoContainer} data-test-id='company-logo-container'>
                {customLogo ?? (
                  <Box
                    component='img'
                    alt={t('auth.alt_portalLogo')}
                    src={companyDataRequest.data?.logo?.url ?? undefined}
                    onError={handleLogoError}
                    sx={sx.companyLogo}
                    data-test-id='company-logo-image'
                  />
                )}
              </Stack>
            )}
            <Stack alignItems='center' spacing={0.5}>
              <Typography variant='h6Bold' sx={sx.title}>
                {title}
              </Typography>
              {!!subtitle && (
                <Typography variant='body2' sx={sx.subtitle}>
                  {subtitle}
                </Typography>
              )}
            </Stack>
          </Stack>
          {children({ companySubdomain: companyDataRequest.data?.subdomain ?? null })}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default AuthLayout;
